import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@features/auth/contexts/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import HeadLayout from "@shared/layouts/HeadLayout";
import useTitle from "@shared/hooks/useTitle";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import { startOfDay, endOfDay, subDays } from "date-fns";
import {
  PackageIcon,
  DollarSign,
  RefreshCw,
  Users,
  TrendingUp,
  Building,
  MapPin,
  Target,
  Plus,
  Truck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOverview } from "@features/dashboard/hooks/useStatstics";
import { useIsMobile } from "@shared/hooks/use-mobile";
import { SelectUser } from "@/features/users/SelectUser";
import { SelectCity } from "@/features/settings/citys/SelectCity";
import DashboardErrorBoundary from "@features/dashboard/components/DashboardErrorBoundary";
import { ChartBarLabel } from "@features/dashboard/components/chart-bar-label";
import InvoiceStatusTable from "@features/dashboard/components/InvoiceStatusTable";
import PackagesStatusTable from "@features/dashboard/components/PackagesStatusTable";
import { DashboardCardsSkeleton } from "@features/dashboard/components/dashboard-cards";
import CardStatusMobile from "@features/dashboard/components/CardStatusMobile";
import { STATUS_COLORS, STATUS_OPTIONS } from "@shared/utils/constants";

import StatCard from "../components/StatCard";
import PackageCard from "../components/PackageCard";

export default function AdminDashboard() {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  const [params, setParams] = useState({
    from: startOfDay(subDays(new Date(), 1000)), // Last 1000 days like client dashboard
    to: endOfDay(new Date()),
  });

  const {
    data: stats,
    isLoading,
    refetch,
  } = useOverview(params, {
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });

  console.log("Admin Dashboard Statistics:", stats);

  useTitle(t("navigation.dashboard"));

  const handleAddDate = (date) => {
    if (!date?.from || !date?.to) {
      return;
    }

    // Validate date range (max 1 year)
    const maxDays = 1000;
    const daysDiff = Math.ceil(
      (date.to.getTime() - date.from.getTime()) / (24 * 60 * 60 * 1000)
    );

    if (daysDiff > maxDays) {
      // We don't have toast anymore, so just console.warn
      console.warn(
        `Date range too large (${daysDiff} days). Maximum allowed is ${maxDays} days.`
      );
      return;
    }

    setParams((prev) => ({
      ...prev,
      from: startOfDay(date.from),
      to: endOfDay(date.to),
    }));
  };

  const handleAddUser = (user) => {
    setParams((prev) => ({
      ...prev,
      user: user,
    }));
  };

  const handleAddCity = (city) => {
    setParams((prev) => ({
      ...prev,
      city: city.name,
    }));
  };

  const handleRefresh = async () => {
    try {
      await refetch(true); // Use enhanced refetch with toast
    } catch (error) {
      console.error("Dashboard refresh error:", error);
      // Error handling is now done in the enhanced refetch function
    }
  };

  return (
    <DashboardErrorBoundary>
      <div className="space-y-6">
        <HeadLayout title={t("navigation.dashboard")}>
          <div className="flex items-center gap-2">
            <DatePickerWithRange
              initialDate={params.date}
              onSelectDate={handleAddDate}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center gap-2"
              aria-label={t("common.refresh")}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              {!isMobile && t("common.refresh")}
            </Button>
          </div>
        </HeadLayout>

        {/* Filters Section */}
        <div className="flex flex-col sm:flex-row gap-2 items-center justify-start">
          {auth.isAdmin && (
            <SelectUser
              user={params.user}
              onSelectUser={handleAddUser}
              role={"client"}
            />
          )}
          <SelectCity onSelectCity={handleAddCity} />
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <DashboardCardsSkeleton />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-1/3 animate-pulse"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] bg-muted rounded animate-pulse"></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-1/3 animate-pulse"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] bg-muted rounded animate-pulse"></div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : stats ? (
          <>
            {/* Admin-specific Welcome Section */}
            <Card className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                      {t("dashboard.adminWelcome", {
                        name: auth.user?.name,
                      }) || `Welcome back, ${auth.user?.name || "Admin"}`}
                    </h2>
                    <p className="text-purple-700 dark:text-purple-300 mt-1">
                      {t("dashboard.adminWelcomeMessage") ||
                        "Monitor and manage your delivery operations"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate("/packages")}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <PackageIcon className="h-4 w-4 mr-2" />
                      {t("packages.viewAll") || "View Packages"}
                    </Button>
                    <Button
                      onClick={() => navigate("/users")}
                      variant="outline"
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      {t("users.manage") || "Manage Users"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Admin-specific Statistics Cards */}
            <div className="space-y-6">
              {/* Business Overview */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  {t("dashboard.businessOverview") || "Business Overview"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <StatCard
                    title={t("dashboard.totalPackages")}
                    value={`${stats.packages?.totalPackagesInRange || 0}`}
                    icon={PackageIcon}
                    color="blue"
                    trend={
                      (stats.packages?.totalPackagesInRange || 0) > 0
                        ? `${stats.packages?.deliveryRate || 0}% ${
                            t("dashboard.delivered") || "delivered"
                          }`
                        : undefined
                    }
                  />
                  <StatCard
                    title={t("dashboard.totalRevenue")}
                    value={`${
                      stats.amounts?.totalRevenue?.toFixed(2) || "0.00"
                    } MAD`}
                    icon={DollarSign}
                    color="green"
                    trend={
                      stats.amounts?.profitMargin > 0
                        ? `${stats.amounts?.profitMargin}% ${
                            t("dashboard.profitMargin") || "profit margin"
                          }`
                        : undefined
                    }
                  />
                  <StatCard
                    title={t("dashboard.activeClients") || "Active Clients"}
                    value={`${stats.clients?.total || 0}`}
                    icon={Users}
                    color="purple"
                  />
                </div>
              </div>

              {/* Operational Metrics */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  {t("dashboard.operationalMetrics") || "Operational Metrics"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <StatCard
                    title={t("dashboard.totalInvoices") || "Total Invoices"}
                    value={`${stats.invoices?.totalInvoices || 0}`}
                    icon={TrendingUp}
                    color="blue"
                    trend={
                      stats.invoices?.paymentRate > 0
                        ? `${stats.invoices?.paymentRate}% ${
                            t("dashboard.paid") || "paid"
                          }`
                        : undefined
                    }
                  />
                  <StatCard
                    title={t("dashboard.paidInvoices") || "Paid Invoices"}
                    value={`${stats.invoices?.totalPaid || 0}`}
                    icon={MapPin}
                    color="orange"
                  />
                  <StatCard
                    title={t("dashboard.totalProfit") || "Total Profit"}
                    value={`${stats.amounts?.profit?.toFixed(2) || "0.00"} MAD`}
                    icon={Users}
                    color="green"
                  />
                  <StatCard
                    title={t("dashboard.totalCost") || "Total Cost"}
                    value={`${
                      stats.amounts?.totalCost?.toFixed(2) || "0.00"
                    } MAD`}
                    icon={Building}
                    color="purple"
                  />
                  <StatCard
                    title={t("dashboard.totalLivreurs") || "Total Livreurs"}
                    value={`${stats.livreurs?.total || 0}`}
                    icon={Truck}
                    color="indigo"
                  />
                  <StatCard
                    title={t("dashboard.deliveryRate") || "Delivery Rate"}
                    value={`${stats.packages?.deliveryRate || 0}%`}
                    icon={Target}
                    color={
                      (stats.packages?.deliveryRate || 0) >= 90
                        ? "green"
                        : (stats.packages?.deliveryRate || 0) >= 70
                        ? "orange"
                        : "red"
                    }
                  />
                </div>
              </div>
            </div>

            {/* Package Status Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PackageIcon className="h-5 w-5" aria-hidden="true" />
                  {t("dashboard.packagesStatusChart")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartBarLabel data={stats.packageStatus} />
              </CardContent>
            </Card>

            {/* Package Status Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PackageIcon className="h-5 w-5" aria-hidden="true" />
                  {t("dashboard.packagesStatus")}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <PackagesStatusTable data={stats.packageStatus} />
              </CardContent>
            </Card>

            {/* Mobile Status Cards */}
            {isMobile && (
              <div className="space-y-4">
                <CardStatusMobile data={stats.packageStatus} />
              </div>
            )}

            {/* Recent Activity Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <PackageIcon className="h-5 w-5" aria-hidden="true" />
                    {t("dashboard.recentPackages")}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/packages")}
                  >
                    {t("common.viewAll")}
                  </Button>
                </CardHeader>
                <CardContent className="p-4">
                  {isLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <Card
                          key={i}
                          className="border-l-2 border-l-transparent"
                        >
                          <CardContent className="p-3">
                            <div className="animate-pulse">
                              {/* Compact header skeleton */}
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 bg-muted rounded"></div>
                                  <div className="w-16 h-4 bg-muted rounded"></div>
                                </div>
                                <div className="w-16 h-4 bg-muted rounded"></div>
                              </div>

                              {/* Customer info skeleton */}
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex-1">
                                  <div className="h-4 bg-muted rounded w-2/3 mb-1"></div>
                                  <div className="h-3 bg-muted rounded w-1/2"></div>
                                </div>
                                <div className="w-12 h-3 bg-muted rounded"></div>
                              </div>

                              {/* Tracking skeleton */}
                              <div className="bg-muted/30 rounded px-2 py-1 border border-dashed">
                                <div className="h-3 bg-muted rounded w-1/2"></div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : stats.lastPackages && stats.lastPackages.length > 0 ? (
                    <div className="space-y-3">
                      {stats.lastPackages.map((pkg) => (
                        <PackageCard key={pkg._id} pkg={pkg} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <PackageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{t("packages.noPackages")}</p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => navigate("/packages/create")}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {t("packages.createFirst")}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" aria-hidden="true" />
                    {t("dashboard.invoiceStatus")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  {/* Invoice Status Breakdown */}
                  <InvoiceStatusTable
                    data={stats.invoiceStatus}
                    isLoading={isLoading}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Package Status Overview - Admin specific */}
            {stats?.packages && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" aria-hidden="true" />
                    {t("dashboard.packageStatusOverview") ||
                      "Package Status Overview"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Status List */}
                  {stats.packageStatus?.map((status) => (
                    <div
                      key={status._id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Badge
                          className={STATUS_COLORS[status._id]}
                          variant="outline"
                        >
                          {t(STATUS_OPTIONS[status._id]) || status._id}
                        </Badge>
                      </div>
                      <span className="font-medium">{status.count}</span>
                    </div>
                  ))}

                  {/* Performance metrics */}
                  {stats && (
                    <div className="mt-4 pt-3 border-t border-muted">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            {t("dashboard.deliveryRate") || "Delivery Rate"}
                          </span>
                          <span
                            className={`font-medium ${
                              (stats.packages?.deliveryRate || 0) >= 90
                                ? "text-green-600"
                                : (stats.packages?.deliveryRate || 0) >= 70
                                ? "text-orange-600"
                                : "text-red-600"
                            }`}
                          >
                            {stats.packages?.deliveryRate || 0}%
                          </span>
                        </div>

                        {stats.amounts?.profitMargin > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              {t("dashboard.profitMargin") || "Profit Margin"}
                            </span>
                            <span className="font-medium text-purple-600">
                              {stats.amounts?.profitMargin}%
                            </span>
                          </div>
                        )}

                        {stats.invoices?.paymentRate > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              {t("dashboard.paymentRate") || "Payment Rate"}
                            </span>
                            <span
                              className={`font-medium ${
                                (stats.invoices?.paymentRate || 0) >= 90
                                  ? "text-green-600"
                                  : (stats.invoices?.paymentRate || 0) >= 70
                                  ? "text-orange-600"
                                  : "text-red-600"
                              }`}
                            >
                              {stats.invoices?.paymentRate}%
                            </span>
                          </div>
                        )}

                        {stats.packages?.delivered > 0 &&
                          stats.amounts?.totalAmount && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">
                                {t("dashboard.avgOrderValue") || "Avg Order"}
                              </span>
                              <span className="font-medium text-blue-600">
                                {(
                                  stats.amounts.totalAmount /
                                  stats.packages.delivered
                                ).toFixed(0)}{" "}
                                MAD
                              </span>
                            </div>
                          )}
                      </div>

                      {/* Performance indicators */}
                      <div className="mt-3 pt-2 border-t border-muted/50">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            {t("dashboard.performanceIndicator") ||
                              "Performance"}
                          </span>
                          <div className="flex items-center gap-1">
                            {(stats.packages?.deliveryRate || 0) >= 90 &&
                            (stats.invoices?.paymentRate || 0) >= 90 ? (
                              <>
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-green-600 font-medium">
                                  {t("dashboard.excellent") || "Excellent"}
                                </span>
                              </>
                            ) : (stats.packages?.deliveryRate || 0) >= 70 &&
                              (stats.invoices?.paymentRate || 0) >= 70 ? (
                              <>
                                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                                <span className="text-orange-600 font-medium">
                                  {t("dashboard.good") || "Good"}
                                </span>
                              </>
                            ) : (
                              <>
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                <span className="text-red-600 font-medium">
                                  {t("dashboard.needsImprovement") ||
                                    "Needs Improvement"}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Pickup Management Section */}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="text-muted-foreground text-center">
              <PackageIcon
                className="h-12 w-12 mx-auto mb-4 opacity-50"
                aria-hidden="true"
              />
              <h3 className="text-lg font-medium">{t("dashboard.noData")}</h3>
              <p className="text-sm">{t("dashboard.noDataDescription")}</p>
            </div>
            <Button
              onClick={handleRefresh}
              variant="outline"
              aria-label={t("common.retry")}
            >
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
              {t("common.retry")}
            </Button>
          </div>
        )}
      </div>
    </DashboardErrorBoundary>
  );
}
